import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Play, Square, RefreshCw, Archive, Database, Settings,
    Wifi, HardDrive, Cpu, TerminalSquare, AlertTriangle, UserPlus, Image as ImageIcon,
    ShieldCheck, CheckCircle2, XCircle, Circle, Save, ChevronDown, ChevronRight, Loader2, User,
    MessageCircle, Video, Home, ClipboardList, CheckSquare, PenTool, FileText,
    UploadCloud, Trash2, CreditCard, Package, ShoppingCart, GraduationCap, Bus, Power, CalendarCheck
} from 'lucide-react';
import { Terminal } from '@xterm/xterm';
import io from 'socket.io-client';
import '@xterm/xterm/css/xterm.css';
import { getClientById, uploadClientLogo, updateClient, injectClasses } from '../services/clientService';
import { startContainer, stopContainer, restartContainer } from '../services/containerService';
import { useOutletContext } from 'react-router-dom';
import CreateAdminForm from '../components/CreateAdminForm';
import ClientStatus from '../components/ClientStatus';
import SSHTab from '../components/SSHTab';
import UploadsTab from '../components/UploadsTab';
import MongoTab from '../components/MongoTab';
import SharedSSHUnlock from '../components/SharedSSHUnlock';
import BackupTab from '../components/BackupTab';
import MonitoringTab from '../components/MonitoringTab';
import { SOCKET_URL } from '../config/env';
const TenantDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { activeTab, setActiveTab } = useOutletContext() || { activeTab: 'monitor' };
    const [tenant, setTenant] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [sshSessionId, setSshSessionId] = useState(null);
    const [sshStatus, setSshStatus] = useState('idle');
    const [sshError, setSshError] = useState('');
    const [realIsRunning, setRealIsRunning] = useState(null);
    const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(true);

    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [expandedSection, setExpandedSection] = useState('modules');
    const [modules, setModules] = useState({
        module_chat: 1, module_visio: 1, module_custom_home: 1,
        module_activites: 1, module_qcm: 1, module_devoir: 1, module_document: 1,
        module_finance: 1, module_depots: 1, module_produits: 1, module_scolarite: 1, module_bus: 1, module_disponibilites: 0
    });
    const [moduleSaving, setModuleSaving] = useState(false);
    const [moduleSaveMsg, setModuleSaveMsg] = useState('');
    const [containerStates, setContainerStates] = useState({});

    // Injection classes state
    const [selectedClasses, setSelectedClasses] = useState([]);
    const [classInjectionLoading, setClassInjectionLoading] = useState(false);
    const [classInjectionMsg, setClassInjectionMsg] = useState('');

    const PREDEFINED_CLASSES = {
        'Primaire (Ecole)': [
            'السنة الأولى ابتدائي', 'السنة الثانية ابتدائي', 'السنة الثالثة ابتدائي', 
            'السنة الرابعة ابتدائي', 'السنة الخامسة ابتدائي', 'السنة السادسة ابتدائي'
        ],
        'Collège': [
            '7ème année de base', '8ème année de base', '9ème année de base'
        ],
        'Lycée': [
            'titre:- 1ère année secondaire',
            '1ère année secondaire', 
            'titre:- 2ème année secondaire',
            '2ème année Lettres', '2ème année Sciences',
            '2ème année Économie et Services', '2ème année Technologies de l\'Informatique',
            'titre:- 3ème année secondaire',
            '3ème année Lettres', '3ème année Mathématiques', '3ème année Sciences Expérimentales',
            '3ème année Sciences Techniques', '3ème année Économie et Gestion', '3ème année Informatique',
            'titre:- 4ème année secondaire (Baccalauréat)',
            'Bac Lettres', 'Bac Mathématiques', 'Bac Sciences Expérimentales',
            'Bac Sciences Techniques', 'Bac Économie et Gestion', 'Bac Informatique'
        ],
        'Formation & Langues (CECR)': [
            'titre:- Niveaux Standards (CECR)',
            'A1', 'A2', 'B1', 'B2', 'C1', 'C2',
            'titre:- Niveaux Détaillés / Sous-niveaux',
            'A1.1', 'A1.2', 'A2.1', 'A2.2',
            'B1.1', 'B1.2', 'B2.1', 'B2.2',
            'C1.1', 'C1.2', 'C2.1', 'C2.2',
            'titre:- Intitulés Complets',
            'Niveau A1 (Débutant)', 'Niveau A2 (Élémentaire)',
            'Niveau B1 (Intermédiaire)', 'Niveau B2 (Intermédiaire Supérieur)',
            'Niveau C1 (Avancé)', 'Niveau C2 (Maîtrise)',
            'titre:- Niveaux Généraux de Formation',
            'Débutant', 'Intermédiaire', 'Avancé', 'Expert',
            'Initiation', 'Perfectionnement'
        ],
        'Université & Écoles d\'Ingénieurs': [
            'titre:- Cycle Licence (LMD)',
            'Licence 1 – Tronc Commun Informatique',
            'Licence 2 – Génie Logiciel',
            'Licence 2 – Systèmes d\'Information',
            'Licence 2 – Réseaux et Systèmes',
            'Licence 2 – Business Intelligence',
            'Licence 3 – Génie Logiciel',
            'Licence 3 – Systèmes d\'Information',
            'Licence 3 – Réseaux et Systèmes',
            'Licence 3 – Business Intelligence',
            'Licence 3 – E-Business',
            'titre:- Cycle Master',
            'Master 1 – Génie Logiciel',
            'Master 2 – Génie Logiciel',
            'Master 1 – Systèmes d\'Information',
            'Master 2 – Systèmes d\'Information',
            'Master 1 – Intelligence Artificielle',
            'Master 2 – Intelligence Artificielle',
            'Master 1 – Data Science',
            'Master 2 – Data Science',
            'Master 1 – Cybersécurité',
            'Master 2 – Cybersécurité',
            'Master 1 – Réseaux et Systèmes',
            'Master 2 – Réseaux et Systèmes',
            'titre:- Cycle Ingénieur',
            '1ère année ingénieur – Tronc Commun',
            '2ème année ingénieur – Tronc Commun',
            '3ème année ingénieur – Génie Informatique',
            '3ème année ingénieur – Ingénierie Logicielle',
            '3ème année ingénieur – Systèmes et Réseaux',
            '3ème année ingénieur – Intelligence Artificielle',
            '3ème année ingénieur – Systèmes Embarqués',
            '3ème année ingénieur – Cybersécurité'
        ],
        'Enseignement Supérieur & Professionnel': [
            'titre:- Cycle Universitaire (Général LMD)',
            'Licence 1 (L1)', 'Licence 2 (L2)', 'Licence 3 (L3)',
            'Master 1 (M1)', 'Master 2 (M2)',
            'titre:- Diplômes Professionnels',
            'CAP', 'BTP', 'BTS'
        ]
    };

    // Terminal handling
    const terminalRef = useRef(null);
    const termInstance = useRef(null);
    const socket = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        fetchTenantDetails();
    }, [id]);

    useEffect(() => {
        if (!loading && terminalRef.current && !termInstance.current) {
            const term = new Terminal({
                theme: {
                    background: '#0f172a',
                    foreground: '#e2e8f0',
                    cursor: '#3b82f6',
                    selection: 'rgba(59, 130, 246, 0.3)',
                    black: '#000000',
                    red: '#ef4444',
                    green: '#10b981',
                    yellow: '#f59e0b',
                    blue: '#3b82f6',
                    magenta: '#8b5cf6',
                    cyan: '#06b6d4',
                    white: '#ffffff'
                },
                fontFamily: '"Fira Code", "Courier New", monospace',
                fontSize: 12,
                cursorBlink: true,
                disableStdin: true,
                cols: 120,
                rows: 30,
            });

            termInstance.current = term;
            term.open(terminalRef.current);

            term.writeln('\x1b[1;36m[XORA ORCHESTRATOR V2.4]\x1b[0m');
            term.writeln(`\x1b[2mInitiating secure shell for instance ${id}...\x1b[0m`);

            let lastLogs = null;

            const renderLogs = (data) => {
                if (data && typeof data.logs !== 'undefined') {
                    // We don't clear anymore to allow continuous streaming
                    const cleanStr = data.logs.replace(/[^\x20-\x7E\n\r\t\x1B]/g, '');
                    const lines = cleanStr.split('\n');

                    lines.forEach(line => {
                        if (line.trim()) {
                            let coloredLine = line;
                            const upperLine = line.toUpperCase();
                            if (upperLine.includes('ERROR') || upperLine.includes('ERR!') || upperLine.includes('FAIL')) {
                                coloredLine = `\x1b[1;31m${line}\x1b[0m`; // Red
                            } else if (upperLine.includes('WARN')) {
                                coloredLine = `\x1b[1;33m${line}\x1b[0m`; // Yellow
                            } else if (upperLine.includes('INFO') || upperLine.includes('SUCCESS') || upperLine.includes('READY')) {
                                coloredLine = `\x1b[1;32m${line}\x1b[0m`; // Green
                            }
                            term.writeln(coloredLine);
                        }
                    });
                }
            };

            // Connect via socket for real-time log streaming
            const logSocket = io(SOCKET_URL, { withCredentials: true, transports: ['websocket'] });
            socket.current = logSocket;

            logSocket.on('logs:update', renderLogs);
            logSocket.on('logs:error', (msg) => {
                term.writeln(`\x1b[1;31m[SSH ERROR] ${msg}\x1b[0m`);
            });

            // Global SSH Connection Listeners
            logSocket.on('ssh:connected', (data) => {
                setSshSessionId(data.sessionId);
                setSshStatus('connected');
                setSshError('');

                // Fetch real status!
                if (tenant && tenant.DbName) {
                    logSocket.emit('ssh:checkStatus', { sessionId: data.sessionId, dbName: tenant.DbName });
                }
            });
            logSocket.on('ssh:error', (msg) => {
                setSshStatus('error');
                setSshError(msg);
                if (id) {
                    sessionStorage.removeItem(`ssh_pass_${id}`);
                }
            });
            logSocket.on('connect', () => {
                const savedPass = id ? sessionStorage.getItem(`ssh_pass_${id}`) : null;
                if (savedPass) {
                    setSshStatus('idle');
                }
            });

            logSocket.on('disconnect', () => {
                setSshStatus('idle');
                setSshSessionId(null);
            });

            logSocket.on('ssh:closed', () => {
                setSshStatus('idle');
                setSshSessionId(null);
                setRealIsRunning(null);
            });

            logSocket.on('ssh:statusResult', (data) => {
                if (!data.dbName || !tenant?.DbName || data.dbName === tenant.DbName) {
                    setRealIsRunning(data.isRunning);
                    if (data.containers) {
                        setContainerStates(data.containers);
                    }
                }
            });

            logSocket.on('ssh:actionResult', (data) => {
                if (data.success) {
                    term.writeln(`\x1b[1;32m[SYSTEM] Action exécutée avec succès.\x1b[0m`);
                    if (data.result) term.writeln(`\x1b[2m${data.result}\x1b[0m`);
                    fetchTenantDetails();

                    if (sshSessionId && tenant?.DbName) {
                        logSocket.emit('ssh:checkStatus', { sessionId: sshSessionId, dbName: tenant.DbName });
                        setTimeout(() => {
                            logSocket.emit('ssh:startLogs', { sessionId: sshSessionId, dbName: tenant.DbName });
                        }, 1200);
                    }
                } else {
                    term.writeln(`\x1b[1;31m[SYSTEM ERROR] Action échouée : ${data.error}\x1b[0m`);
                }
                setActionLoading(false);
            });

            return () => {
                logSocket.disconnect();
                socket.current = null;
                if (termInstance.current) termInstance.current.dispose();
                termInstance.current = null;
            };
        }
    }, [loading, id, tenant]);

    const handleGlobalSSHConnect = (ip, username, password) => {
        if (!socket.current) return;

        if (id && password) {
            sessionStorage.setItem(`ssh_pass_${id}`, password);
        }
        setSshStatus('connecting');
        setSshError('');

        // Socket.IO buffers emit calls automatically if connecting
        socket.current.emit('ssh:connect', { ip, username, password });
    };

    // Safety timeout: reset connecting state if SSH doesn't respond within 18 seconds
    useEffect(() => {
        let timer;
        if (sshStatus === 'connecting') {
            timer = setTimeout(() => {
                setSshStatus('error');
                setSshError('Délai d\'attente de connexion SSH dépassé (Timeout). Vérifiez si l\'adresse IP est correcte et accessible.');
                if (id) {
                    sessionStorage.removeItem(`ssh_pass_${id}`);
                }
            }, 18000);
        }
        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [sshStatus, id]);

    // Auto-connect effect if session password exists and status is idle
    useEffect(() => {
        if (tenant && id && sshStatus === 'idle' && socket.current) {
            const savedPass = sessionStorage.getItem(`ssh_pass_${id}`);
            if (savedPass) {
                const ip = tenant.vpsId || tenant.ip || 'localhost';
                if (socket.current.connected) {
                    handleGlobalSSHConnect(ip, 'ubuntu', savedPass);
                } else {
                    const handleConnect = () => {
                        handleGlobalSSHConnect(ip, 'ubuntu', savedPass);
                    };
                    socket.current.once('connect', handleConnect);
                }
            }
        }
    }, [tenant, id, sshStatus]);

    // Trigger status check on SSH session activation
    useEffect(() => {
        if (socket.current && sshSessionId && tenant?.DbName) {
            socket.current.emit('ssh:checkStatus', { sessionId: sshSessionId, dbName: tenant.DbName });
        }
    }, [sshSessionId, tenant?.DbName]);

    const handleGlobalSSHDisconnect = () => {
        if (id) {
            sessionStorage.removeItem(`ssh_pass_${id}`);
        }
        if (tenant?.id) {
            sessionStorage.removeItem(`ssh_pass_${tenant.id}`);
        }
        if (sshSessionId && socket.current) {
            socket.current.emit('ssh:disconnect', { sessionId: sshSessionId });
        }
        setSshStatus('disconnected');
        setSshSessionId(null);
        setRealIsRunning(null);
    };

    const isLocal = !tenant?.vpsId || tenant?.vpsId === 'local' || tenant?.vpsId === '127.0.0.1' || tenant?.vpsId === 'localhost';
    const sshRequired = !isLocal && sshStatus !== 'connected';

    // Manage SSH stream lifecycle
    useEffect(() => {
        let currentStreamId = null;
        const currentSocket = socket.current;
        if (activeTab === 'monitor' && sshSessionId && tenant && currentSocket) {
            if (termInstance.current) {
                termInstance.current.clear();
                termInstance.current.writeln('\x1b[1;36m[XORA ORCHESTRATOR V2.4] \x1b[1;32m[SSH CONNECTED]\x1b[0m');
                termInstance.current.writeln(`\x1b[2mDémarrage du flux de logs pour ${tenant.DbName}...\x1b[0m\n`);
            }

            const onLogsStarted = (data) => {
                currentStreamId = data.streamSessionId;
            };
            currentSocket.on('ssh:logsStarted', onLogsStarted);
            currentSocket.emit('ssh:startLogs', { sessionId: sshSessionId, dbName: tenant.DbName });

            return () => {
                if (currentSocket) {
                    currentSocket.off('ssh:logsStarted', onLogsStarted);
                    if (currentStreamId) {
                        currentSocket.emit('ssh:stopLogs', { streamSessionId: currentStreamId });
                    }
                }
            };
        }
    }, [activeTab, sshSessionId, tenant]);

    const fetchTenantDetails = async () => {
        try {
            const data = await getClientById(id);
            // Map the returned client payload to the format expected by the UI. 
            // Ensures safety if the names changed.
            setTenant({
                ...data,
                id: data._id || data.id,
                domain: data.domain || data.name,
                schoolName: data.name || data.dbName,
                version: '1.0',
                status: data.status || 'running',
                createdAt: data.createdAt || new Date()
            });
            setModules({
                module_chat: data.module_chat ?? 1,
                module_visio: data.module_visio ?? 1,
                module_custom_home: data.module_custom_home ?? 1,
                module_activites: data.module_activites ?? 1,
                module_qcm: data.module_qcm ?? 1,
                module_devoir: data.module_devoir ?? 1,
                module_document: data.module_document ?? 1,
                module_finance: data.module_finance ?? 1,
                module_depots: data.module_depots ?? 1,
                module_produits: data.module_produits ?? 1,
                module_scolarite: data.module_scolarite ?? 1,
                module_bus: data.module_bus ?? 1,
                module_disponibilites: data.module_disponibilites ?? 0
            });
            setLoading(false);
        } catch (error) {
            console.error('Error fetching tenant details:', error);
            navigate('/dashboard');
        }
    };

    const MODULE_OPTIONS = [
        { key: 'module_chat', label: 'Chat & Messaging', description: 'Real-time communication', icon: MessageCircle, color: 'text-violet-500' },
        { key: 'module_visio', label: 'Video Meetings', description: 'Integrated video conferencing', icon: Video, color: 'text-blue-500' },
        { key: 'module_custom_home', label: 'Custom Homepage', description: 'Branded landing experience', icon: Home, color: 'text-orange-500' },
        { key: 'module_activites', label: 'Activities Management', description: 'Track tasks and events', icon: ClipboardList, color: 'text-amber-500' },
        { key: 'module_qcm', label: 'Quizzes & Exams', description: 'Assessment tools (QCM)', icon: CheckSquare, color: 'text-emerald-500' },
        { key: 'module_devoir', label: 'Assignments', description: 'Homework & corrections', icon: PenTool, color: 'text-rose-500' },
        { key: 'module_document', label: 'Document Center', description: 'Secure file sharing', icon: FileText, color: 'text-indigo-500' },
        { key: 'module_finance', label: 'Finance & Paiements', description: 'Gestion financière et paiements', icon: CreditCard, color: 'text-green-500' },
        { key: 'module_depots', label: 'Gestion des Dépôts', description: 'Gestion de stock', icon: Package, color: 'text-yellow-500' },
        { key: 'module_produits', label: 'Catalogue Produits', description: 'Gestion des produits', icon: ShoppingCart, color: 'text-cyan-500' },
        { key: 'module_scolarite', label: 'Scolarité', description: 'Bulletins et semestres', icon: GraduationCap, color: 'text-pink-500' },
        { key: 'module_bus', label: 'Transport Scolaire', description: 'Gestion des bus', icon: Bus, color: 'text-sky-500' },
        { key: 'module_disponibilites', label: 'Disponibilités', description: 'Disponibilités des professeurs (désactivé par défaut)', icon: CalendarCheck, color: 'text-teal-500' },
    ];

    const handleModuleToggle = (key) => {
        setModules(prev => ({ ...prev, [key]: prev[key] === 1 ? 0 : 1 }));
    };

    const handleSaveModules = async () => {
        setModuleSaving(true);
        setModuleSaveMsg('');
        try {
            await updateClient(id, modules);
            setModuleSaveMsg('Modules mis à jour !');
            setTimeout(() => setModuleSaveMsg(''), 3000);
        } catch (err) {
            setModuleSaveMsg('Erreur lors de la sauvegarde.');
        } finally {
            setModuleSaving(false);
        }
    };

    const handleInjectClasses = async () => {
        if (selectedClasses.length === 0) {
            setClassInjectionMsg('Sélectionnez au moins une classe.');
            return;
        }
        setClassInjectionLoading(true);
        setClassInjectionMsg('');
        try {
            const res = await injectClasses(id, selectedClasses);
            setClassInjectionMsg(`${res.results.success.length} succès, ${res.results.failed.length} échecs.`);
            setTimeout(() => setClassInjectionMsg(''), 4000);
        } catch (err) {
            setClassInjectionMsg('Erreur lors de l\'injection.');
        } finally {
            setClassInjectionLoading(false);
        }
    };

    const toggleClassSelection = (cls) => {
        setSelectedClasses(prev => 
            prev.includes(cls) ? prev.filter(c => c !== cls) : [...prev, cls]
        );
    };

    const selectAllCategory = (categoryClasses) => {
        const validClasses = categoryClasses.filter(c => !c.startsWith('titre:'));
        const allSelected = validClasses.every(c => selectedClasses.includes(c));
        if (allSelected) {
            setSelectedClasses(prev => prev.filter(c => !validClasses.includes(c)));
        } else {
            const newSelections = validClasses.filter(c => !selectedClasses.includes(c));
            setSelectedClasses(prev => [...prev, ...newSelections]);
        }
    };

    const handleAction = async (action) => {
        setActionLoading(action);

        try {
            if (isLocal) {
                // Actions locales via l'API REST
                let res;
                if (action === 'start') res = await startContainer(tenant.id);
                else if (action === 'stop') res = await stopContainer(tenant.id);
                else if (action === 'restart') res = await restartContainer(tenant.id);

                if (termInstance.current) {
                    termInstance.current.writeln(`\x1b[1;32m[SYSTEM] Action ${action.toUpperCase()} locale réussie.\x1b[0m`);
                }
                fetchTenantDetails();
                setActionLoading(false);
            } else {
                // Actions distantes via SSH
                const savedPass = sessionStorage.getItem(`ssh_pass_${id}`);

                if ((!sshSessionId || sshStatus !== 'connected') && savedPass && tenant) {
                    const ip = tenant.vpsId || tenant.ip || 'localhost';
                    if (termInstance.current) {
                        termInstance.current.writeln(`\x1b[1;33m[SYSTEM] Reconnexion SSH automatique avant l'action ${action.toUpperCase()}...\x1b[0m`);
                    }
                    handleGlobalSSHConnect(ip, 'ubuntu', savedPass);

                    const handleConnected = (data) => {
                        if (socket.current) {
                            socket.current.off('ssh:connected', handleConnected);
                            socket.current.emit('ssh:executeAction', { sessionId: data.sessionId, dbName: tenant.DbName, action });
                            if (termInstance.current) {
                                termInstance.current.writeln(`\x1b[1;33m[SYSTEM] Exécution de l'action ${action.toUpperCase()} via SSH...\x1b[0m`);
                            }
                        }
                    };
                    if (socket.current) {
                        socket.current.once('ssh:connected', handleConnected);
                    }
                    return;
                }

                if (!sshSessionId || !socket.current) {
                    alert("Cette instance est hébergée sur un serveur distant.\n\nVeuillez d'abord vous connecter au terminal SSH ci-dessous pour effectuer cette action.");
                    setActionLoading(false);
                    return;
                }

                socket.current.emit('ssh:executeAction', { sessionId: sshSessionId, dbName: tenant.DbName, action });
                if (termInstance.current) {
                    termInstance.current.writeln(`\x1b[1;33m[SYSTEM] Exécution de l'action ${action.toUpperCase()} via SSH...\x1b[0m`);
                }
            }
        } catch (error) {
            console.error(`Error performing action ${action}:`, error);
            if (termInstance.current) {
                termInstance.current.writeln(`\x1b[1;31m[SYSTEM ERROR] Action échouée : ${error.message || error}\x1b[0m`);
            }
            setActionLoading(false);
        }
    };


    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const removeLogo = () => {
        setLogoFile(null);
        setLogoPreview(null);
    };

    const handleUploadLogo = async (e) => {
        e.preventDefault();
        if (!logoFile) return;
        setModalLoading(true);
        try {
            await uploadClientLogo(id, logoFile);
            setLogoFile(null);
            setLogoPreview(null);
            fetchTenantDetails();
            if (termInstance.current) {
                termInstance.current.writeln(`\x1b[1;32m[SYSTEM] Tenant logo updated successfully.\x1b[0m`);
            }
        } catch (error) {
            console.error('Error uploading logo:', error);
            if (termInstance.current) {
                termInstance.current.writeln(`\x1b[1;31m[ERROR] Failed to update logo.\x1b[0m`);
            }
        } finally {
            setModalLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex-1 flex items-center justify-center min-h-screen bg-slate-50">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Synchronisation...</div>
                </div>
            </div>
        );
    }

    const isRunning = realIsRunning !== null ? realIsRunning : (tenant?.status?.toLowerCase() === 'running');

    let badgeColor = 'border-slate-200 text-slate-500 bg-slate-100';
    let displayStatus = tenant?.status || 'UNKNOWN';

    if (realIsRunning === true) {
        displayStatus = 'RUNNING';
        badgeColor = 'border-emerald-200 text-emerald-600 bg-emerald-50';
    } else if (realIsRunning === false) {
        displayStatus = 'STOPPED';
        badgeColor = 'border-slate-200 text-slate-500 bg-slate-100';
    } else if (tenant?.status?.toLowerCase() === 'running') {
        badgeColor = 'border-emerald-200 text-emerald-600 bg-emerald-50';
    } else if (tenant?.status?.toLowerCase() === 'error') {
        badgeColor = 'border-rose-200 text-rose-600 bg-rose-50';
    }

    return (
        <div className="flex flex-col h-full bg-slate-50 relative overflow-hidden">
            {/* Split Screen Layout */}
            <div className="flex-1 flex overflow-hidden relative">

                {/* Mobile / Tablet Dark Backdrop Overlay when Drawer is Open */}
                {isLeftPanelOpen && (
                    <div
                        onClick={() => setIsLeftPanelOpen(false)}
                        className="lg:hidden fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-40 transition-opacity"
                    />
                )}

                {/* Left Column: Metrics & Identity (Drawer on mobile/tablet, Side-by-side panel on desktop) */}
                <div
                    className={`transition-all duration-300 bg-white flex flex-col shrink-0 z-50 border-r border-slate-200 ${
                        /* Mobile/Tablet: Slide-Over Drawer */
                        'fixed lg:relative inset-y-0 left-0 h-full shadow-2xl lg:shadow-none'
                        } ${isLeftPanelOpen
                            ? 'translate-x-0 w-[300px] sm:w-[340px] lg:w-[320px] xl:w-[360px] 2xl:w-[390px]'
                            : '-translate-x-full lg:translate-x-0 lg:w-0 lg:border-r-0 opacity-100 lg:opacity-0 overflow-hidden'
                        }`}
                >
                    {/* Identity Block */}
                    <div className="p-4 sm:p-5 border-b border-slate-200 shrink-0">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <div className="text-[10px] font-black tracking-widest text-slate-400 uppercase">Fiche Instance</div>
                                <button
                                    onClick={() => setIsLeftPanelOpen(false)}
                                    className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
                                    title="Masquer le volet"
                                >
                                    <ChevronRight size={16} className="rotate-180" />
                                </button>
                            </div>
                            <div className={`px-2 py-0.5 text-[9px] font-black uppercase tracking-widest border ${badgeColor}`}>
                                {displayStatus}
                            </div>
                        </div>
                        <div className="flex items-start justify-between">
                            <div>
                                <h1 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight leading-none mb-1">{tenant.schoolName}</h1>
                                <div className="text-xs font-mono text-slate-500 mb-3">{tenant.domain}</div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Version Logiciel</div>
                                <div className="text-xs sm:text-sm font-bold text-slate-800">v{tenant.version}</div>
                            </div>
                            <div>
                                <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Création</div>
                                <div className="text-xs sm:text-sm font-bold text-slate-800">
                                    {new Date(tenant.createdAt).toLocaleDateString()}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Port & Services Registry */}
                    <div className="flex-1 overflow-y-auto">
                        <div className="px-5 py-3 border-b border-slate-100">
                            <div className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Registre des Ports</div>
                        </div>
                        {[
                            { label: 'Backend API', port: tenant.serverPort, icon: <Wifi size={14} />, defaultColor: 'text-blue-500', statusKey: 'backend' },
                            { label: 'Frontend Web', port: tenant.frontendPort, icon: <HardDrive size={14} />, defaultColor: 'text-emerald-500', statusKey: 'frontend' },
                            { label: 'Database Mongo', port: tenant.mongoPort || (tenant.serverPort ? tenant.serverPort + 8 : null), icon: <Database size={14} />, defaultColor: 'text-violet-500', statusKey: 'mongo' },
                        ].map(({ label, port, icon, defaultColor, statusKey }) => {
                            const isLive = containerStates[statusKey] !== undefined
                                ? containerStates[statusKey]
                                : (tenant.containerStatuses?.[statusKey] === 'Running' || isRunning);
                            const dotColor = isLive ? 'bg-emerald-400 animate-pulse' : 'bg-rose-400';

                            return (
                                <div key={label} className="border-b border-slate-100 px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-2 h-2 rounded-full shrink-0 ${dotColor}`} />
                                        <div className={defaultColor}>{icon}</div>
                                        <div className="text-[10px] font-black text-slate-600 uppercase tracking-widest">{label}</div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        {isLive ? (
                                            <span className="px-2 py-0.5 rounded text-[9px] font-extrabold bg-emerald-50 text-emerald-700 border border-emerald-200/60 flex items-center gap-1">
                                                Running <CheckCircle2 size={10} className="text-emerald-500" />
                                            </span>
                                        ) : (
                                            <span className="px-2 py-0.5 rounded text-[9px] font-extrabold bg-rose-50 text-rose-700 border border-rose-200/60 flex items-center gap-1">
                                                Stopped <XCircle size={10} className="text-rose-500" />
                                            </span>
                                        )}
                                        <div className="font-mono text-xs font-black text-slate-900 w-12 text-right">
                                            {port ? <>:{port}</> : <span className="text-slate-300">—</span>}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        <div className="px-5 py-3 border-b border-slate-100 mt-2">
                            <div className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Conteneurs Jitsi & Services</div>
                        </div>
                        {[
                            { label: 'Jitsi Web', key: 'jitsiWebContainerId', statusKey: 'jitsiWeb', portOffset: 2, icon: <Video size={14} />, defaultColor: 'text-indigo-500' },
                            { label: 'Prosody', key: 'jitsiProsodyContainerId', statusKey: 'jitsiProsody', portOffset: 6, icon: <MessageCircle size={14} />, defaultColor: 'text-cyan-500' },
                            { label: 'Jicofo', key: 'jitsiJicofoContainerId', statusKey: 'jitsiJicofo', portOffset: 4, icon: <Cpu size={14} />, defaultColor: 'text-amber-500' },
                            { label: 'JVB', key: 'jitsiJvbContainerId', statusKey: 'jitsiJvb', portOffset: 5, icon: <Wifi size={14} />, defaultColor: 'text-purple-500' },
                            { label: 'Whiteboard', key: 'jitsiWhiteboardContainerId', statusKey: 'jitsiWhiteboard', portOffset: 7, icon: <PenTool size={14} />, defaultColor: 'text-rose-500' },
                        ].map(({ label, key, statusKey, portOffset, icon, defaultColor }) => {
                            const port = tenant[`${key.replace('ContainerId', '')}Port`] || (tenant.serverPort ? tenant.serverPort + portOffset : null);
                            const altKey = statusKey.replace(/^jitsi/, '').toLowerCase();
                            const isLive = containerStates[statusKey] !== undefined
                                ? containerStates[statusKey]
                                : (containerStates[altKey] !== undefined
                                    ? containerStates[altKey]
                                    : (tenant.containerStatuses?.[statusKey] === 'Running'));
                            const dotColor = isLive ? 'bg-emerald-400 animate-pulse' : 'bg-rose-400';

                            return (
                                <div key={key} className="border-b border-slate-100 px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-2 h-2 rounded-full shrink-0 ${dotColor}`} />
                                        <div className={defaultColor}>{icon}</div>
                                        <div className="text-[10px] font-black text-slate-600 uppercase tracking-widest">{label}</div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        {isLive ? (
                                            <span className="px-2 py-0.5 rounded text-[9px] font-extrabold bg-emerald-50 text-emerald-700 border border-emerald-200/60 flex items-center gap-1">
                                                Running <CheckCircle2 size={10} className="text-emerald-500" />
                                            </span>
                                        ) : (
                                            <span className="px-2 py-0.5 rounded text-[9px] font-extrabold bg-rose-50 text-rose-700 border border-rose-200/60 flex items-center gap-1">
                                                Stopped <XCircle size={10} className="text-rose-500" />
                                            </span>
                                        )}
                                        <div className="font-mono text-xs font-black text-slate-900 w-12 text-right">
                                            {port ? <>:{port}</> : <span className="text-slate-300">—</span>}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        <div className="border-b border-slate-100 px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
                            <div className="flex items-center gap-3">
                                <Database size={14} className="text-violet-500 shrink-0" />
                                <div>
                                    <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Nom de la Base</div>
                                    <div className="font-mono text-sm font-black text-slate-900">{tenant.DbName || '—'}</div>
                                </div>
                            </div>
                            <div className={`w-2 h-2 rounded-full shrink-0 bg-violet-400`} />
                        </div>
                        <div className="border-b border-slate-100 px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
                            <div className="flex items-center gap-3">
                                <Cpu size={14} className="text-orange-400 shrink-0" />
                                <div>
                                    <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Nom VPS</div>
                                    <div className="font-mono text-sm font-black text-slate-900">{tenant.vpsId || '—'}</div>
                                </div>
                            </div>
                            <div className={`w-2 h-2 rounded-full shrink-0 bg-orange-300`} />
                        </div>
                        <div className="border-b border-slate-100 px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
                            <div className="flex items-center gap-3">
                                <User size={14} className="text-green-400 shrink-0" />
                                <div>
                                    <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Nombre d'utilisateurs maximaux</div>
                                    <div className="font-mono text-sm font-black text-slate-900">{tenant.maxUsers || '—'}</div>
                                </div>
                            </div>
                            <div className={`w-2 h-2 rounded-full shrink-0 bg-green-300`} />
                        </div>
                    </div>
                </div>

                {/* Right Column: Controls & Dynamic Tabs */}
                <div className="flex-1 flex flex-col bg-slate-50 relative min-h-0 min-w-0">

                    {/* Monitor Log Stream Tab (Always mounted for terminal persistence) */}
                    <div className={activeTab === 'monitor' ? 'flex-1 flex flex-col absolute inset-0' : 'hidden'}>
                        {/* Action Center HUD (Top row) */}
                        <div className="h-20 shrink-0 flex border-b border-slate-200 bg-white overflow-x-auto">
                            {!isLeftPanelOpen && (
                                <button
                                    onClick={() => setIsLeftPanelOpen(true)}
                                    className="px-4 border-r border-slate-200 flex flex-col items-center justify-center gap-1 bg-slate-50 hover:bg-slate-100 text-slate-700 transition-all shrink-0 font-bold"
                                    title="Déplier Fiche Instance"
                                >
                                    <ChevronRight size={18} />
                                    <span className="text-[9px] font-black uppercase tracking-widest">Fiche</span>
                                </button>
                            )}

                            <button
                                disabled={isRunning || !!actionLoading || sshRequired}
                                onClick={() => handleAction('start')}
                                className={`flex-1 border-r border-slate-200 flex flex-col items-center justify-center gap-2 transition-all ${isRunning || !!actionLoading || sshRequired ? 'opacity-50 cursor-not-allowed bg-slate-50 text-slate-400' : 'hover:bg-emerald-50 text-emerald-600 hover:text-emerald-700'
                                    }`}
                            >
                                {actionLoading === 'start' ? <Loader2 size={18} className="animate-spin text-emerald-600" /> : <Play size={18} className={isRunning || !!actionLoading || sshRequired ? 'text-slate-300' : ''} />}
                                <span className="text-[10px] font-black uppercase tracking-widest">{sshRequired ? 'SSH REQUIS' : 'Initialiser'}</span>
                            </button>

                            <button
                                disabled={!isRunning || !!actionLoading || sshRequired}
                                onClick={() => handleAction('stop')}
                                className={`flex-1 border-r border-slate-200 flex flex-col items-center justify-center gap-2 transition-all ${!isRunning || !!actionLoading || sshRequired ? 'opacity-50 cursor-not-allowed bg-slate-50 text-slate-400' : 'hover:bg-rose-50 text-rose-600 hover:text-rose-700'
                                    }`}
                            >
                                {actionLoading === 'stop' ? <Loader2 size={18} className="animate-spin text-rose-600" /> : <Square size={18} className={!isRunning || !!actionLoading || sshRequired ? 'text-slate-300' : ''} />}
                                <span className="text-[10px] font-black uppercase tracking-widest">{sshRequired ? 'SSH REQUIS' : 'Interrompre'}</span>
                            </button>

                            <button
                                disabled={!!actionLoading || sshRequired}
                                onClick={() => handleAction('restart')}
                                className={`flex-1 border-r border-slate-200 flex flex-col items-center justify-center gap-2 transition-all ${!!actionLoading || sshRequired ? 'opacity-50 cursor-not-allowed bg-slate-50 text-slate-400' : 'hover:bg-blue-50 text-blue-600 hover:text-blue-700'}`}
                            >
                                {actionLoading === 'restart' ? <Loader2 size={18} className="animate-spin text-blue-600" /> : <RefreshCw size={18} className={!!actionLoading || sshRequired ? 'text-slate-300' : ''} />}
                                <span className="text-[10px] font-black uppercase tracking-widest">{sshRequired ? 'SSH REQUIS' : 'Redémarrer'}</span>
                            </button>

                            <div className="flex-1 flex flex-col items-center justify-center gap-2 bg-slate-50 text-slate-400">
                                <Archive size={18} />
                                <span className="text-[10px] font-black uppercase tracking-widest">Backup</span>
                            </div>
                        </div>

                        {/* The Signature Element: Raw Terminal Frame */}
                        <div className="flex-1 bg-slate-900 flex flex-col relative min-h-0">
                            {/* Terminal Header */}
                            <div className="h-12 border-b border-slate-800 flex justify-between items-center px-6 bg-[#0B1120] shrink-0">
                                <div className="flex items-center gap-3">
                                    <TerminalSquare size={16} className="text-slate-500" />
                                    <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">/var/log/instance.log</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className={`w-2 h-2 rounded-full ${isRunning ? 'bg-emerald-500' : 'bg-slate-600'}`}></div>
                                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Live Stream</span>
                                    {sshStatus === 'connected' && (
                                        <button
                                            onClick={handleGlobalSSHDisconnect}
                                            className="ml-2 flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold text-rose-400 bg-rose-950/40 hover:bg-rose-900/60 border border-rose-800/50 rounded transition-all shadow-xs"
                                            title="Déconnecter le SSH"
                                        >
                                            <Power size={11} />
                                            <span>Déconnecter</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                            {/* Terminal Body */}
                            <div className="flex-1 overflow-hidden relative flex flex-col">
                                <div className="flex-1 p-4 relative z-0 bg-[#0f172a]">
                                    <div ref={terminalRef} className="w-full h-full pb-8" />
                                    <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-[#0f172a] to-transparent pointer-events-none"></div>
                                </div>

                                {(() => {
                                    const hasSavedPass = id ? !!sessionStorage.getItem(`ssh_pass_${id}`) : false;
                                    const showUnlockModal = sshStatus === 'error' || (!hasSavedPass && sshStatus !== 'connected');
                                    return showUnlockModal ? (
                                        <div className="absolute inset-0 z-10 flex flex-col">
                                            <SharedSSHUnlock
                                                tenant={tenant}
                                                status={sshStatus}
                                                errorMessage={sshError}
                                                onConnect={handleGlobalSSHConnect}
                                            />
                                        </div>
                                    ) : null;
                                })()}
                            </div>
                        </div>
                    </div>

                    {activeTab === 'database' && (
                        <div className="flex-1 flex flex-col absolute inset-0 overflow-hidden bg-slate-50">
                            {/* Page Header (Fixed / Sticky) */}
                            <div className="h-16 px-4 lg:px-6 border-b border-slate-200 flex items-center justify-between bg-white shrink-0 z-10 shadow-xs">
                                <div className="flex items-center gap-3">
                                    {!isLeftPanelOpen && (
                                        <button
                                            onClick={() => setIsLeftPanelOpen(true)}
                                            className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg border border-slate-200 transition-colors shrink-0"
                                            title="Déplier Fiche Instance"
                                        >
                                            <ChevronRight size={18} />
                                        </button>
                                    )}
                                    <div className="p-2.5 bg-violet-50 text-violet-600 rounded-lg border border-violet-100">
                                        <Database size={20} />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h3 className="text-base font-bold text-slate-800 tracking-tight">Facturation</h3>
                                            <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-violet-100 text-violet-700 font-mono">
                                                {tenant?.DbName || 'Database'}
                                            </span>
                                        </div>
                                        <div className="text-xs text-slate-500 font-medium flex items-center gap-2 mt-0.5">
                                            <span>Statut de l'Instance • {tenant?.schoolName}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Scrollable Content Body */}
                            <div className="flex-1 overflow-y-auto p-6 w-full">
                                <ClientStatus clientId={id} tenantName={tenant.schoolName} />
                            </div>
                        </div>
                    )}

                    {activeTab === 'settings' && (
                        <div className="flex-1 flex flex-col absolute inset-0 overflow-hidden bg-white">
                            {/* Page Header (Fixed / Sticky) */}
                            <div className="h-16 px-4 lg:px-6 border-b border-slate-200 flex items-center justify-between bg-white shrink-0 z-10 shadow-xs">
                                <div className="flex items-center gap-3">
                                    {!isLeftPanelOpen && (
                                        <button
                                            onClick={() => setIsLeftPanelOpen(true)}
                                            className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg border border-slate-200 transition-colors shrink-0"
                                            title="Déplier Fiche Instance"
                                        >
                                            <ChevronRight size={18} />
                                        </button>
                                    )}
                                    <div className="p-2.5 bg-slate-100 text-slate-700 rounded-lg border border-slate-200">
                                        <Settings size={20} />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h3 className="text-base font-bold text-slate-800 tracking-tight">Configuration Instance</h3>
                                            <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 font-mono">
                                                v{tenant?.version}
                                            </span>
                                        </div>
                                        <div className="text-xs text-slate-500 font-medium flex items-center gap-2 mt-0.5">
                                            <span>{tenant?.schoolName} • {tenant?.domain}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Scrollable Content Body - Split Pane Design */}
                            <div className="flex-1 overflow-hidden flex bg-slate-50/50">
                                {/* Settings Navigation Sidebar */}
                                <div className="w-80 shrink-0 border-r border-slate-200 bg-white overflow-y-auto p-6 flex flex-col gap-2">
                                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-3">Menu de Configuration</h4>
                                    
                                    <button
                                        onClick={() => setExpandedSection('modules')}
                                        className={`flex items-center justify-between w-full p-4 rounded-xl transition-all duration-200 border ${
                                            expandedSection === 'modules' || !expandedSection 
                                            ? 'bg-emerald-50/50 border-emerald-100 shadow-sm ring-1 ring-emerald-500/10' 
                                            : 'bg-transparent border-transparent hover:bg-slate-50'
                                        }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`p-2 rounded-lg ${expandedSection === 'modules' || !expandedSection ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20' : 'bg-slate-100 text-slate-500'}`}>
                                                <ShieldCheck size={16} />
                                            </div>
                                            <div className="text-left">
                                                <h5 className={`text-[11px] font-black uppercase tracking-widest ${expandedSection === 'modules' || !expandedSection ? 'text-emerald-900' : 'text-slate-700'}`}>Modules</h5>
                                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{Object.values(modules).filter(v => v === 1).length} Activés</p>
                                            </div>
                                        </div>
                                    </button>

                                    <button
                                        onClick={() => setExpandedSection('classes')}
                                        className={`flex items-center justify-between w-full p-4 rounded-xl transition-all duration-200 border ${
                                            expandedSection === 'classes' 
                                            ? 'bg-indigo-50/50 border-indigo-100 shadow-sm ring-1 ring-indigo-500/10' 
                                            : 'bg-transparent border-transparent hover:bg-slate-50'
                                        }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`p-2 rounded-lg ${expandedSection === 'classes' ? 'bg-indigo-500 text-white shadow-md shadow-indigo-500/20' : 'bg-slate-100 text-slate-500'}`}>
                                                <GraduationCap size={16} />
                                            </div>
                                            <div className="text-left">
                                                <h5 className={`text-[11px] font-black uppercase tracking-widest ${expandedSection === 'classes' ? 'text-indigo-900' : 'text-slate-700'}`}>Classes & Niveaux</h5>
                                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Injection Rapide</p>
                                            </div>
                                        </div>
                                    </button>

                                    <button
                                        onClick={() => setExpandedSection('admin')}
                                        className={`flex items-center justify-between w-full p-4 rounded-xl transition-all duration-200 border ${
                                            expandedSection === 'admin' 
                                            ? 'bg-blue-50/50 border-blue-100 shadow-sm ring-1 ring-blue-500/10' 
                                            : 'bg-transparent border-transparent hover:bg-slate-50'
                                        }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`p-2 rounded-lg ${expandedSection === 'admin' ? 'bg-blue-500 text-white shadow-md shadow-blue-500/20' : 'bg-slate-100 text-slate-500'}`}>
                                                <UserPlus size={16} />
                                            </div>
                                            <div className="text-left">
                                                <h5 className={`text-[11px] font-black uppercase tracking-widest ${expandedSection === 'admin' ? 'text-blue-900' : 'text-slate-700'}`}>Administrateur</h5>
                                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Créer un compte</p>
                                            </div>
                                        </div>
                                    </button>

                                    <button
                                        onClick={() => setExpandedSection('logo')}
                                        className={`flex items-center justify-between w-full p-4 rounded-xl transition-all duration-200 border ${
                                            expandedSection === 'logo' 
                                            ? 'bg-violet-50/50 border-violet-100 shadow-sm ring-1 ring-violet-500/10' 
                                            : 'bg-transparent border-transparent hover:bg-slate-50'
                                        }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`p-2 rounded-lg ${expandedSection === 'logo' ? 'bg-violet-500 text-white shadow-md shadow-violet-500/20' : 'bg-slate-100 text-slate-500'}`}>
                                                <ImageIcon size={16} />
                                            </div>
                                            <div className="text-left">
                                                <h5 className={`text-[11px] font-black uppercase tracking-widest ${expandedSection === 'logo' ? 'text-violet-900' : 'text-slate-700'}`}>Logo</h5>
                                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Personnalisation</p>
                                            </div>
                                        </div>
                                    </button>
                                </div>

                                {/* Settings Content Area */}
                                <div className="flex-1 overflow-y-auto relative bg-slate-50/50">
                                    <div className="max-w-4xl mx-auto w-full px-8 lg:px-12 pb-8 lg:pb-12">
                                        
                                        {/* MODULES SECTION */}
                                        {(!expandedSection || expandedSection === 'modules') && (
                                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                                                <div className="flex items-center justify-between border-b border-slate-200 pb-5 pt-8 lg:pt-12 sticky top-0 z-20 bg-slate-50/95 backdrop-blur-sm">
                                                    <div>
                                                        <h2 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-3">
                                                            Modules Applicatifs
                                                            <span className="text-[10px] font-black bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full uppercase tracking-widest">Feature Toggles</span>
                                                        </h2>
                                                        <p className="text-xs text-slate-500 font-medium tracking-wide mt-1">Activez ou désactivez les fonctionnalités pour cette école.</p>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        {moduleSaveMsg && (
                                                            <span className="text-[10px] font-black uppercase tracking-widest text-emerald-600">{moduleSaveMsg}</span>
                                                        )}
                                                        <button
                                                            onClick={handleSaveModules}
                                                            disabled={moduleSaving}
                                                            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white text-[11px] font-black uppercase tracking-widest rounded-lg hover:bg-emerald-700 shadow-md shadow-emerald-500/20 transition-all disabled:opacity-50"
                                                        >
                                                            <Save size={14} />
                                                            {moduleSaving ? 'Sauvegarde...' : 'Sauvegarder'}
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                                    {MODULE_OPTIONS.map(({ key, label, description, icon: Icon, color }) => {
                                                        const isActive = modules[key] === 1;
                                                        return (
                                                            <button
                                                                key={key}
                                                                type="button"
                                                                onClick={() => handleModuleToggle(key)}
                                                                className={`group relative flex items-start gap-4 p-5 rounded-2xl text-left transition-all duration-300 border ${
                                                                    isActive
                                                                    ? 'bg-white border-emerald-200 shadow-sm ring-2 ring-emerald-500/10'
                                                                    : 'bg-white/60 border-slate-200 hover:bg-white hover:border-slate-300 hover:shadow-sm grayscale hover:grayscale-0'
                                                                }`}
                                                            >
                                                                <div className={`flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-300 ${isActive ? 'bg-slate-50 scale-110 shadow-sm' : 'bg-slate-100 opacity-50'}`}>
                                                                    <Icon size={20} className={isActive ? color : 'text-slate-400'} strokeWidth={1.5} />
                                                                </div>
                                                                <div className="flex-1 min-w-0 mt-0.5">
                                                                    <h5 className={`text-sm font-bold tracking-tight truncate ${isActive ? 'text-slate-900' : 'text-slate-600'}`}>
                                                                        {label}
                                                                    </h5>
                                                                    <p className="text-[10px] font-semibold tracking-wider text-slate-500 uppercase mt-1 leading-relaxed line-clamp-2">
                                                                        {description}
                                                                    </p>
                                                                </div>
                                                                <div className="flex items-center justify-center pt-2">
                                                                    {isActive ? (
                                                                        <div className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-md shadow-emerald-500/30">
                                                                            <CheckCircle2 size={12} strokeWidth={3} />
                                                                        </div>
                                                                    ) : (
                                                                        <Circle size={20} className="text-slate-300" strokeWidth={1.5} />
                                                                    )}
                                                                </div>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </motion.div>
                                        )}

                                        {/* CLASSES SECTION */}
                                        {expandedSection === 'classes' && (
                                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                                                <div className="flex items-center justify-between border-b border-slate-200 pb-5 pt-8 lg:pt-12 sticky top-0 z-20 bg-slate-50/95 backdrop-blur-sm">
                                                    <div>
                                                        <h2 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-3">
                                                            Injection de Classes & Niveaux
                                                            <span className="text-[10px] font-black bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full uppercase tracking-widest">Base de données</span>
                                                        </h2>
                                                        <p className="text-xs text-slate-500 font-medium tracking-wide mt-1">Sélectionnez les classes ou niveaux de formation à injecter directement dans la base de données du tenant.</p>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        {classInjectionMsg && (
                                                            <span className="text-[10px] font-black uppercase tracking-widest text-indigo-600">{classInjectionMsg}</span>
                                                        )}
                                                        <button
                                                            onClick={handleInjectClasses}
                                                            disabled={classInjectionLoading || selectedClasses.length === 0}
                                                            className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white text-[11px] font-black uppercase tracking-widest rounded-lg hover:bg-indigo-700 shadow-md shadow-indigo-500/20 transition-all disabled:opacity-50"
                                                        >
                                                            {classInjectionLoading ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                                                            {classInjectionLoading ? 'Injection...' : 'Injecter la sélection'}
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="space-y-8">
                                                    {Object.entries(PREDEFINED_CLASSES).map(([category, classesList]) => {
                                                        const validClasses = classesList.filter(c => !c.startsWith('titre:'));
                                                        const allSelected = validClasses.every(c => selectedClasses.includes(c));
                                                        return (
                                                            <div key={category} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                                                                <div className="flex items-center justify-between mb-5">
                                                                    <div className="flex items-center gap-3">
                                                                        <div className="w-1.5 h-6 bg-indigo-500 rounded-full"></div>
                                                                        <h4 className="text-sm font-bold text-slate-800 uppercase tracking-widest">{category}</h4>
                                                                    </div>
                                                                    <button 
                                                                        onClick={() => selectAllCategory(classesList)}
                                                                        className="px-3 py-1.5 text-[10px] font-black bg-slate-100 text-indigo-600 rounded-md hover:bg-indigo-50 transition-colors uppercase tracking-widest"
                                                                    >
                                                                        {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                                                                    </button>
                                                                </div>
                                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                                                    {classesList.map(cls => {
                                                                        if (cls.startsWith('titre:')) {
                                                                            return (
                                                                                <div key={cls} className="col-span-full mt-4 mb-2">
                                                                                    <h5 className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{cls.replace('titre:', '')}</h5>
                                                                                </div>
                                                                            );
                                                                        }
                                                                        const isSelected = selectedClasses.includes(cls);
                                                                        return (
                                                                            <button type="button" onClick={() => toggleClassSelection(cls)} key={cls} className={`flex items-center text-left gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200 border ${isSelected ? 'bg-indigo-50/50 border-indigo-200 ring-1 ring-indigo-500/20' : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`}>
                                                                                <div className={`shrink-0 w-5 h-5 rounded-md flex items-center justify-center transition-colors ${isSelected ? 'bg-indigo-500 text-white' : 'border border-slate-300 bg-white'}`}>
                                                                                    {isSelected && <CheckSquare size={12} strokeWidth={3} />}
                                                                                </div>
                                                                                <span className={`text-xs font-bold transition-colors ${isSelected ? 'text-indigo-900' : 'text-slate-600'}`}>{cls}</span>
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </motion.div>
                                        )}

                                        {/* ADMIN SECTION */}
                                        {expandedSection === 'admin' && (
                                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                                                <div className="flex items-center justify-between border-b border-slate-200 pb-5 pt-8 lg:pt-12 sticky top-0 z-20 bg-slate-50/95 backdrop-blur-sm">
                                                    <div>
                                                        <h2 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-3">
                                                            Créer un Administrateur
                                                            <span className="text-[10px] font-black bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full uppercase tracking-widest">Sécurité</span>
                                                        </h2>
                                                        <p className="text-xs text-slate-500 font-medium tracking-wide mt-1">Créez le compte d'administration principal pour ce tenant.</p>
                                                    </div>
                                                </div>
                                                <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm max-w-2xl mx-auto">
                                                    <CreateAdminForm
                                                        clientId={id}
                                                        clientName={tenant?.schoolName}
                                                        onSuccess={() => {
                                                            if (termInstance.current) {
                                                                termInstance.current.writeln('\x1b[1;32m[SYSTEM] Admin account created successfully.\x1b[0m');
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </motion.div>
                                        )}

                                        {/* LOGO SECTION */}
                                        {expandedSection === 'logo' && (
                                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                                                <div className="flex items-center justify-between border-b border-slate-200 pb-5 pt-8 lg:pt-12 sticky top-0 z-20 bg-slate-50/95 backdrop-blur-sm">
                                                    <div>
                                                        <h2 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-3">
                                                            Personnalisation
                                                            <span className="text-[10px] font-black bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full uppercase tracking-widest">Marque Blanche</span>
                                                        </h2>
                                                        <p className="text-xs text-slate-500 font-medium tracking-wide mt-1">Modifiez le logo principal de la plateforme.</p>
                                                    </div>
                                                </div>
                                                
                                                <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm max-w-xl mx-auto">
                                                    <form onSubmit={handleUploadLogo} className="space-y-6">
                                                        <div>
                                                            <div className="flex items-center justify-center w-full mb-6">
                                                                {logoPreview ? (
                                                                    <div className="relative group/logo">
                                                                        <div className="w-32 h-32 border-2 border-slate-100 bg-white p-2 overflow-hidden rounded-2xl shadow-sm">
                                                                            <img src={logoPreview} alt="Logo Preview" className="w-full h-full object-contain" />
                                                                        </div>
                                                                        <button
                                                                            type="button"
                                                                            onClick={removeLogo}
                                                                            className="absolute -top-3 -right-3 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg transform hover:scale-110 transition-all"
                                                                        >
                                                                            <Trash2 size={14} />
                                                                        </button>
                                                                    </div>
                                                                ) : (
                                                                    <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 hover:border-violet-400 cursor-pointer transition-all rounded-2xl group">
                                                                        <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm mb-3 group-hover:scale-110 transition-transform">
                                                                            <UploadCloud size={20} className="text-violet-500" />
                                                                        </div>
                                                                        <p className="text-[11px] font-bold text-slate-600 uppercase tracking-widest">Cliquez pour ajouter un logo</p>
                                                                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">PNG, SVG (Max 5MB)</p>
                                                                        <input type="file" accept="image/*" className="hidden" ref={fileInputRef} onChange={handleFileChange} />
                                                                    </label>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <button type="submit" disabled={modalLoading || !logoFile} className="w-full px-6 py-4 text-[11px] uppercase font-black tracking-widest bg-violet-600 text-white hover:bg-violet-700 shadow-md shadow-violet-500/20 transition-all disabled:opacity-50 disabled:shadow-none rounded-xl flex justify-center items-center gap-2">
                                                            {modalLoading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                                            {modalLoading ? 'Téléchargement...' : 'Enregistrer le logo'}
                                                        </button>
                                                    </form>
                                                </div>
                                            </motion.div>
                                        )}

                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Monitoring Live Metrics Tab (Always mounted for state persistence) */}
                    <div className={activeTab === 'metrics' ? 'flex-1 flex flex-col absolute inset-0 bg-white' : 'hidden'}>
                        <MonitoringTab
                            tenant={tenant}
                            isVisible={activeTab === 'metrics'}
                            sshSessionId={sshSessionId}
                            socket={socket.current}
                            sshStatus={sshStatus}
                            sshError={sshError}
                            onConnect={handleGlobalSSHConnect}
                            onDisconnect={handleGlobalSSHDisconnect}
                            isLeftPanelOpen={isLeftPanelOpen}
                            onToggleLeftPanel={() => setIsLeftPanelOpen(true)}
                        />
                    </div>

                    {/* SSH Terminal Tab (Always mounted for state persistence) */}
                    <div className={activeTab === 'ssh' ? 'flex-1 flex flex-col absolute inset-0 bg-white' : 'hidden'}>
                        <SSHTab
                            tenant={tenant}
                            isVisible={activeTab === 'ssh'}
                            sshSessionId={sshSessionId}
                            socket={socket.current}
                            sshStatus={sshStatus}
                            sshError={sshError}
                            onConnect={handleGlobalSSHConnect}
                            onDisconnect={handleGlobalSSHDisconnect}
                            isLeftPanelOpen={isLeftPanelOpen}
                            onToggleLeftPanel={() => setIsLeftPanelOpen(true)}
                        />
                    </div>

                    {/* Uploads Explorer Tab */}
                    <div className={activeTab === 'uploads' ? 'flex-1 flex flex-col absolute inset-0 bg-white' : 'hidden'}>
                        <UploadsTab
                            tenant={tenant}
                            isVisible={activeTab === 'uploads'}
                            sshSessionId={sshSessionId}
                            socket={socket.current}
                            sshStatus={sshStatus}
                            sshError={sshError}
                            onConnect={handleGlobalSSHConnect}
                            onDisconnect={handleGlobalSSHDisconnect}
                            isLeftPanelOpen={isLeftPanelOpen}
                            onToggleLeftPanel={() => setIsLeftPanelOpen(true)}
                        />
                    </div>

                    {/* MongoDB Shell Tab */}
                    <div className={activeTab === 'mongoshell' ? 'flex-1 flex flex-col absolute inset-0 bg-white' : 'hidden'}>
                        <MongoTab
                            tenant={tenant}
                            isVisible={activeTab === 'mongoshell'}
                            sshSessionId={sshSessionId}
                            socket={socket.current}
                            sshStatus={sshStatus}
                            sshError={sshError}
                            onConnect={handleGlobalSSHConnect}
                            onDisconnect={handleGlobalSSHDisconnect}
                            isLeftPanelOpen={isLeftPanelOpen}
                            onToggleLeftPanel={() => setIsLeftPanelOpen(true)}
                        />
                    </div>

                    {/* Backup & Restore Tab */}
                    <div className={activeTab === 'backup' ? 'flex-1 flex flex-col absolute inset-0 bg-white' : 'hidden'}>
                        <BackupTab
                            tenant={tenant}
                            isVisible={activeTab === 'backup'}
                            sshSessionId={sshSessionId}
                            socket={socket.current}
                            sshStatus={sshStatus}
                            sshError={sshError}
                            onConnect={handleGlobalSSHConnect}
                            onDisconnect={handleGlobalSSHDisconnect}
                            onTenantUpdate={fetchTenantDetails}
                            isLeftPanelOpen={isLeftPanelOpen}
                            onToggleLeftPanel={() => setIsLeftPanelOpen(true)}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TenantDetail;
